// Controllers.tsx
import React, { useEffect, useState } from "react";
import { FlattenedObject, scanList } from "../../models/Po";
import { useDispatch, useSelector } from "react-redux";
import {
  RecStatus,
  setRecStatus,
  postProcList,
} from "../../redux/recStartedSlice";
import { clearDefects, setProcParams } from "../../redux/defectsSlice";
import { postMachineStatus } from "../../redux/recMachStartedSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { useNavigate } from "react-router-dom";
import Modal from "../ui/Modal";
import Scanner from "../scanner/Scanner";
// 中間実績数コンポーネント
import QtyBeforeSuspensionModal from "../qtyBeforeSuspension/QtyBeforeSuspensionModal";
import QtyBeforeSuspension from "../qtyBeforeSuspension/QtyBeforeSuspension";
// 作業打刻コンポーネント
import TimecardModal from "../timecard/TimecardModal";
import Timecard from "../timecard/Timecard";
// 代替設備コンポーネント
import AlternativeMachineModal from "../alternativeMachine/AlternativeMachineModal";
import AlternativeMachine from "../alternativeMachine/AlternativeMachine";

import { setIsControllerSticky } from "../../redux/controllerStateSlice";
import { setCheckedRowUpdateAt } from "../../redux/procGridSlice";

import { appendAlert } from "../../redux/alertSlice";

// AgGrid
import { GridApi } from "ag-grid-enterprise";
import "./Controllers.css";

// 新しいインポート
import MachineTransferModal from "../machineTransfer/MachineTransferModal";
import MachineTransfer from "../machineTransfer/MachineTransfer";

interface ControllersProps {
  gridApi: GridApi | null;
  columnApi: any | null;
  rowData: FlattenedObject[];
}

const Controllers: React.FC<ControllersProps> = (props) => {
  const dispatch: AppDispatch = useDispatch();

  // ログインユーザーID
  const userId = useSelector(
    (state: RootState) => state.user.userInfo?.user_id ?? ""
  );
  // コントローラー制御
  const isChangeDashboardPlanProcMach = useSelector(
    (state: RootState) => state.controllerState.isChangeDashboardPlanProcMach
  );

  // 実績登録 -------------------------------------------

  // 中間実績数モーダルオープンフラグ
  const [isSuspensionQtyModalOpen, setIsSuspensionQtyModalOpen] =
    useState(false);
  const [suspensionRows, setSuspensionRows] = useState<any>([]);
  const closeSuspensionQtyModal = () => {
    setIsSuspensionQtyModalOpen(false);
    // stickyを有効化
    dispatch(setIsControllerSticky(true));
  };

  // 加工実績一覧のチェック更新日時
  const checkedRowUpdateAt = useSelector(
    (state: RootState) => state.procGrid.checkedRowUpdateAt
  );

  // 実績登録の状態
  const status = useSelector((state: RootState) => state.recStarted.status);
  // 実績登録のボタン
  const handleButtonClick = (newStatus: RecStatus) => {
    // 無効状態の場合は何もしない
    if (status === "disable") {
      return;
    }
    // 機械が開始済みであること
    if (machStatus == null || machStatus.status_code === 0) {
      dispatch(
        appendAlert({
          severity: "warning",
          message: "実績登録を行うには機械を開始状態にしてください。",
          displayTime: 5000,
        })
      );
      return;
    }
    if (newStatus === "suspend" && status === "started") {
      setSuspensionRows(props.gridApi?.getSelectedRows());
      // aggridがモーダルより優先されるのでstickyを無効化
      dispatch(setIsControllerSticky(false));
      setIsSuspensionQtyModalOpen(true);
    } else {
      if (
        (newStatus === "started" &&
          (status === "stopped" || status === "suspend")) ||
        (newStatus === "stopped" &&
          (status === "started" || status === "suspend"))
      ) {
        dispatch(postProcList(props.gridApi, props.columnApi, newStatus, []));
      }
    }
  };

  // 中間実績入力後に実績登録
  const inputCloseSuspensionQtModal = (suspensionQtys: string[]) => {
    dispatch(
      postProcList(props.gridApi, props.columnApi, "suspend", suspensionQtys)
    );
    setIsSuspensionQtyModalOpen(false);
    // stickyを有効化
    dispatch(setIsControllerSticky(true));
  };

  // 加工実績の選択状態からボタンの状態を決定する
  // ステータス 0-未作業, 1-作業中, 2-一時中断, 3-再開, 4-作業完了
  useEffect(() => {
    if (props.gridApi === null) {
      return;
    }

    const selectedProcRowData =
      props.gridApi.getSelectedRows() as FlattenedObject[];

    // 各状態の件数を抽出
    const selectCount0 = selectedProcRowData.filter(
      (d) => d.proc_status === 0
    ).length;
    const selectCount1 = selectedProcRowData.filter(
      (d) => d.proc_status === 1
    ).length;
    const selectCount2 = selectedProcRowData.filter(
      (d) => d.proc_status === 2
    ).length;
    const selectCount3 = selectedProcRowData.filter(
      (d) => d.proc_status === 3
    ).length;
    const selectCount4 = selectedProcRowData.filter(
      (d) => d.proc_status === 4
    ).length;

    // 実選択の場合は実績登録ボタンは無効
    if (selectedProcRowData.length === 0) {
      dispatch(setRecStatus("disable"));
      // 未作・中断のみ選択されている場合は停止
    } else if (
      (selectCount0 > 0 || selectCount2 > 0) &&
      selectCount1 === 0 &&
      selectCount3 === 0 &&
      selectCount4 === 0
    ) {
      dispatch(setRecStatus("stopped"));
      // 開始・再開のみ選択されている場合は開始
    } else if (
      (selectCount1 > 0 || selectCount3 > 0) &&
      selectCount0 === 0 &&
      selectCount2 === 0 &&
      selectCount4 === 0
    ) {
      dispatch(setRecStatus("started"));
      // 開始,再開が存在せず、1件以上中断が存在すれば中断
    } else if (
      selectCount2 > 0 &&
      selectCount0 === 0 &&
      selectCount1 === 0 &&
      selectCount3 === 0 &&
      selectCount4 === 0
    ) {
      dispatch(setRecStatus("suspend"));
      // 上記以外　例えば開始と終了が混在して選択されている場合は無効
    } else {
      dispatch(setRecStatus("disable"));
    }
  }, [checkedRowUpdateAt, isChangeDashboardPlanProcMach, dispatch]);

  // QRコード読み取り -------------------------------------------
  const [isModalOpen, setIsModalOpen] = useState(false);

  // スキャナモーダルオープン
  const openModal = () => {
    if (props.rowData.length > 0) {
      setIsModalOpen(true);
      // aggridがモーダルより優先されるのでstickyを無効化
      dispatch(setIsControllerSticky(false));
    }
  };

  // スキャナモーダルクローズ
  const closeQrModal = () => {
    setIsModalOpen(false);
    // stickyを有効化
    dispatch(setIsControllerSticky(true));
  };

  // 受注Noをチェックをつける
  const handlesetCheck = (scanPoDescNums: scanList[]) => {
    props.gridApi?.forEachNode(function (rowNode) {
      //rowNode.setSelected(false);
      for (let i = 0; i < scanPoDescNums.length; i++) {
        if (rowNode.data.po_desc_num === scanPoDescNums[i].po_desc_num) {
          // 受注Noが一致した場合、チェックをつける
          rowNode.setSelected(true);
          // 見つかった受注Noは検索対象から削除する
          scanPoDescNums.splice(i, 1);
          // ソート順を更新
          console.log(rowNode.data);
          if (rowNode.data.proc_status === 0) {
            //rowNode.setDataValue("sort_order", 1);
            if (rowNode.data.sort_order !== undefined) {
              rowNode.data.sort_order = 1;
            } else {
              console.error("sort_order undefined");
            }
          }
          return;
        }
      }
    });

    // チェック状態の更新日時を更新
    dispatch(setCheckedRowUpdateAt(new Date().toISOString()));

    props.columnApi.applyColumnState({
      state: [],
      defaultState: { sort: null },
    });

    props.columnApi.applyColumnState({
      state: [{ colId: "sort_order", sort: "desc", sortIndex: 0 }],
      defaultState: { sort: null },
    });
  };

  // 不良登録 -------------------------------------------
  const navigate = useNavigate();

  const userPlantCode = useSelector(
    (state: RootState) => state.user.userInfo.plant_code
  );
  // 不良登録ボタン押下時の処理
  const handleDefectsButton = () => {
    if (props.gridApi != null) {
      // stateクリア
      dispatch(clearDefects());
      // agGridからチェックが入った行データを抽出
      const selectedNodes = props.gridApi.getSelectedNodes();
      const machCodes: string[] = machCode.split(",");
      const selectedMachCode = machCodes.length === 1 ? machCodes[0] : "";
      // pfm_users APIから取得したログインユーザの工場コード
      // 工場が選択されていない状態で不良登録ボタン押下時はユーザの工場コードを使する
      const paramPlantCode = plant_code !== "" ? plant_code : userPlantCode;

      // 加工実績を一つのみ選択している場合は、その情報を不良登録画面へ引き継ぐ
      if (selectedNodes.length === 0) {
        // 加工実績画面で選択した工場、加工、機械コードを不良登録のstateにセット
        dispatch(
          setProcParams({
            plantCode: paramPlantCode,
            procCode: proc_code,
            machCode: selectedMachCode,
            poDescNum: "",
          })
        );
        // 不良登録へ画面遷移
        navigate("/defects");
      } else if (selectedNodes.length === 1) {
        // 加工実績画面で選択した工場、加工、機械コードを不良登録のstateにセット
        dispatch(
          setProcParams({
            plantCode: paramPlantCode,
            procCode: proc_code,
            machCode: selectedMachCode,
            poDescNum: selectedNodes[0].data.po_desc_num,
          })
        );
        // 不良登録へ画面遷移
        navigate("/defects");
        // 加工実績が実選択の場合は不良登録画面へ遷移後、バーコードスキャンを表示
      } else {
        // 加工実績を2件以上選択している場合、エラー表示
        dispatch(
          appendAlert({
            severity: "warning",
            message: "加工実績は1件のみ選択してください。",
            displayTime: 5000,
          })
        );
      }
    }
  };

  // 代替設備 -------------------------------------------
  const [isAltMachineModalOpen, setIsAltMachineModalOpen] = useState(false);
  const [isAltMachineButton, setIsAltMachineButton] = useState(false);
  const selectedListCheck = () => {
    if (props.gridApi != null) {
      // agGridからチェックが入った行データを抽出
      const selectedNodes = props.gridApi.getSelectedNodes();
      const selectedProcList = selectedNodes.map((node) => {
        return node.data.proc_code;
      });
      const selectedMachList = selectedNodes.map((node) => {
        return node.data.mach_code;
      });
      const procListCounts = new Set(selectedProcList).size;
      const machListCounts = new Set(selectedMachList).size;

      // 加工状況一覧の件数が1件以上存在するかつ
      // 加工コード、機械コードが1種類の場合のみ有効化
      if (
        selectedNodes.length > 0 &&
        procListCounts === 1 &&
        machListCounts === 1
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const openAltMachineModal = () => {
    if (selectedListCheck()) {
      setIsAltMachineModalOpen(true);
      // aggridがモーダルより優先されるのでstickyを無効化
      dispatch(setIsControllerSticky(false));
    }
  };
  const closeAltMachineModal = () => {
    setIsAltMachineModalOpen(false);
    // stickyを有効化
    dispatch(setIsControllerSticky(true));
  };

  const altMachineModalButton = () => {
    setIsAltMachineButton(false);
  };

  useEffect(() => {
    if (selectedListCheck()) {
      setIsAltMachineButton(true);
    } else {
      setIsAltMachineButton(false);
    }
  }, [checkedRowUpdateAt]);

  // 機械  -------------------------------------------
  // 機械の状態
  const machStatus = useSelector(
    (state: RootState) => state.recMachStarted.machStatus
  );
  // 工場・加工・機械
  const plant_code = useSelector(
    (state: RootState) => state.queryParams.selectedPlantCode
  );
  const proc_code = useSelector(
    (state: RootState) => state.queryParams.selectedProcCode
  );
  const machCode = useSelector(
    (state: RootState) => state.queryParams.value.mach_code
  );

  // 機械の開始ボタン
  const handleMachinStartButtonClick = () => {
    if (machStatus?.status_code === 1 || machStatus === null) {
      return;
    }
    dispatch(postMachineStatus(machStatus, 1, userId));
  };
  // 機械の停止ボタン
  const handleMachinStopButtonClick = () => {
    if (machStatus?.status_code === 0 || machStatus === null) {
      return;
    }
    dispatch(postMachineStatus(machStatus, 0, userId));
  };

  // 作業打刻  -------------------------------------------
  // 作業打刻モーダルの状態を管理
  const [isTimecardModalOpen, setIsTimecardModalOpen] = useState(false);
  const openTimecardModal = () => {
    setIsTimecardModalOpen(true);
    // aggridがモーダルより優先されるのでstickyを無効化
    dispatch(setIsControllerSticky(false));
  };
  const closeTimecardModal = () => {
    setIsTimecardModalOpen(false);
    // stickyを有効化
    dispatch(setIsControllerSticky(true));
  };

  // 機械転送モーダル用の状態
  const [isMachineTransferModalOpen, setIsMachineTransferModalOpen] =
    useState(false);
  const [isMachineTransferButtonActive, setIsMachineTransferButtonActive] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]); // Add this state

  // 機械転送モーダルを開く関数
  const openMachineTransferModal = () => {
    if (machCode === "118") {
      // isMachineTransferButtonActiveの代わりにmachCodeを直接チェック
      // Get selected rows from the grid
      const selectedNodes = props.gridApi?.getSelectedNodes();
      const selectedData = selectedNodes?.map((node) => node.data) || [];
      setSelectedRows(selectedData); // Store selected rows

      setIsMachineTransferModalOpen(true);
      dispatch(setIsControllerSticky(false));
    }
  };

  // 機械転送モーダルを閉じる関数
  const closeMachineTransferModal = () => {
    setIsMachineTransferModalOpen(false);
    dispatch(setIsControllerSticky(true));
  };

  // 機械転送ボタンの表示状態を更新する関数
  useEffect(() => {
    if (props.gridApi) {
      const isMachCode118 = machCode === "118";
      // machCodeが118の場合は常にアクティブにする
      setIsMachineTransferButtonActive(isMachCode118);
    } else {
      setIsMachineTransferButtonActive(false); // gridApiがない場合はボタンを非表示
    }
  }, [props.gridApi, machCode]);

  return (
    <div className="controllers-container controllers-base">
      <div className="rec-item controllers ">
        <div className="icon-container">
          <img src="images/register.svg" alt="Register Icon" className="icon" />
          <p className="button-label">実績登録</p>
        </div>
        <div className="long-vseparator"></div>
        <div className="icon-container">
          <img
            src={
              (status === "stopped" || status === "suspend") &&
              machStatus?.status_code === 1
                ? "images/rec-start.svg"
                : "images/rec-start-disabled.svg"
            }
            alt="Processes Start"
            className="icon myShadow"
            onClick={() => handleButtonClick("started")}
            style={{
              cursor:
                (status === "stopped" || status === "suspend") &&
                machStatus?.status_code === 1
                  ? "pointer"
                  : "not-allowed",
            }}
          />
          <p className="button-label">開始</p>
        </div>
        <div className="icon-container">
          <img
            src={
              machStatus?.status_code === 1 && status === "started"
                ? "images/rec-pause.svg"
                : "images/rec-pause-disabled.svg"
            }
            alt="Processes Pause"
            className="icon myShadow"
            onClick={() => handleButtonClick("suspend")}
            style={{
              cursor:
                machStatus?.status_code === 1 && status === "started"
                  ? "pointer"
                  : "not-allowed",
            }}
          />
          <p className="button-label">中断</p>
        </div>
        <QtyBeforeSuspensionModal
          isOpen={isSuspensionQtyModalOpen}
          onClose={closeSuspensionQtyModal}
        >
          <QtyBeforeSuspension
            onClose={inputCloseSuspensionQtModal}
            suspensionRows={suspensionRows}
          />
        </QtyBeforeSuspensionModal>
        <div className="icon-container">
          <img
            src={
              machStatus?.status_code === 1 && status === "started"
                ? "images/rec-stop.svg"
                : "images/rec-stop-disabled.svg"
            }
            alt="Processes End"
            className="icon myShadow"
            onClick={() => handleButtonClick("stopped")}
            style={{
              cursor:
                machStatus?.status_code === 1 && status === "started"
                  ? "pointer"
                  : "not-allowed",
            }}
          />
          <p className="button-label">終了</p>
        </div>
        <div className="short-vseparator"></div>
        <div className="icon-container" onClick={openModal}>
          <img
            src={
              props.rowData.length > 0
                ? "images/camera.svg"
                : "images/camera-disabled.svg"
            }
            alt="Start Scanner"
            className="icon myShadow"
            style={{
              cursor: props.rowData.length > 0 ? "pointer" : "not-allowed",
            }}
          />
          <p className="button-label">スキャナ起動</p>
        </div>
        <Modal
          isOpen={isModalOpen}
          onClose={closeQrModal}
          width="70vw"
          height="90vh"
          contentClassName={"scanner-modal-content"}
        >
          <Scanner
            isOpen={isModalOpen}
            onClose={closeQrModal}
            handlesetCheck={handlesetCheck}
            rowData={props.rowData}
          />
        </Modal>
      </div>

      <div className="opt-item controllers">
        <div className="icon-container">
          <img
            src="images/defects.svg"
            alt="Register Defects"
            className="icon myShadow"
            onClick={handleDefectsButton}
          />
          <p className="button-label">不良登録</p>
        </div>

        <div className="short-vseparator"></div>
        <div className="icon-container" onClick={openAltMachineModal}>
          {isAltMachineButton ? (
            <img
              src="images/swap.svg"
              alt="Swap Machine"
              className="icon myShadow"
              style={{ cursor: "pointer" }}
            />
          ) : (
            <img
              src="images/swap_disabled.svg"
              alt="Swap Machine"
              className="icon myShadow"
              style={{ cursor: "not-allowed" }}
            />
          )}
          <p className="button-label">代替設備</p>
        </div>
        <AlternativeMachineModal
          isOpen={isAltMachineModalOpen}
          onClose={closeAltMachineModal}
        >
          <AlternativeMachine
            rowData={props.rowData}
            onClose={closeAltMachineModal}
            gridApi={props.gridApi}
            altMachineModalButton={altMachineModalButton}
          />
        </AlternativeMachineModal>

        {/* 機械転送ボタンと区切り線をmachCodeが"118"の場合のみ表示 */}
        {machCode === "118" && (
          <>
            <div className="short-vseparator"></div>
            <div className="icon-container" onClick={openMachineTransferModal}>
              <img
                src="images/transfer.svg" // 常にアクティブな画像を使用
                alt="Machine Transfer"
                className="icon myShadow"
                style={{ cursor: "pointer" }} // 常にポインターカーソルを表示
              />
              <p className="button-label">機械転送</p>
            </div>
          </>
        )}
        <MachineTransferModal
          isOpen={isMachineTransferModalOpen}
          onClose={closeMachineTransferModal}
        >
          <MachineTransfer
            onClose={closeMachineTransferModal}
            selectedRows={selectedRows}
          />
        </MachineTransferModal>
      </div>

      <div className="mach-item controllers">
        <div className="icon-container">
          <img src="images/machine.svg" alt="Machine" className="icon" />
          <p className="button-label">機械</p>
        </div>
        <div className="long-vseparator"></div>
        {/* 機械開始ボタン */}
        <div className="icon-container">
          <img
            src={
              machStatus?.status_code === 0
                ? "images/mach-start.svg"
                : "images/mach-start-disabled.svg"
            }
            alt="Processes Start"
            className="icon myShadow"
            onClick={() => handleMachinStartButtonClick()}
            style={{
              cursor: machStatus?.status_code === 0 ? "pointer" : "not-allowed",
            }}
          />
          <p className="button-label">開始</p>
        </div>
        {/* 機械終了ボタン */}
        <div className="icon-container">
          <img
            src={
              machStatus?.status_code === 1
                ? "images/mach-stop.svg"
                : "images/mach-stop-disabled.svg"
            }
            alt="Processes End"
            className="icon myShadow"
            onClick={() => handleMachinStopButtonClick()}
            style={{
              cursor: machStatus?.status_code === 1 ? "pointer" : "not-allowed",
            }}
          />
          <p className="button-label">終了</p>
        </div>
        <div className="current-datetime">
          {machStatus?.status_code === 1 && machStatus?.start_time
            ? machStatus?.start_time.slice(5, 7) +
              "/" +
              machStatus?.start_time.slice(8, 10) +
              " " +
              machStatus?.start_time.slice(11, 16) +
              "〜"
            : null}
        </div>
      </div>

      <div className="timecard-item controllers">
        <div className="icon-container" onClick={openTimecardModal}>
          <img
            src="images/time-card.svg"
            alt="Time Card"
            className="icon myShadow"
          />
          <p className="button-label">作業打刻</p>
        </div>
      </div>
      <TimecardModal isOpen={isTimecardModalOpen} onClose={closeTimecardModal}>
        <Timecard />
      </TimecardModal>
    </div>
  );
};

export default Controllers;
